'use client';

import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useEffect, useRef, useState } from 'react';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useRouter } from 'next/navigation';
import { useProfileNavigation } from '../../profile/profile-navigation';
import { createOffer } from '@eliadmin/core/src/api/jobportal/create-curriculum-api';
import React from 'react';
import { AddOfferStepper } from './add-offer-stepper';
import { AddOfferStep0 } from './steps/add-offer-step-0';
import { AddOfferStep1 } from './steps/add-offer-step-1';
import { AddOfferStep2 } from './steps/add-offer-step-2';
import { AddOfferStep3 } from './steps/add-offer-step-3';
import { AddOfferStep4 } from './steps/add-offer-step-4';
import { AddOfferControls } from './add-offer-controls';
import { AddOfferStepsBuckets } from './steps/add-offer-steps-validator';
import { deepGet } from '@eliadmin/core/src/utils/misc-utils';
import { uploadSecretFile } from '@eliadmin/core/src/api/video/video-api';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { JobOfferPayOfferDialog } from '../job-offer-pay-offer-dialog';
import { useQuery } from '@tanstack/react-query';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { fetchSlugDetail } from '@eliadmin/core/src/api/slugs/slugs-api';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import type { ProductDetailData } from '@eliadmin/core/src/api/products/types/product-types';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { fetchPaymentTypes } from '@eliadmin/core/src/api/jobportal/offers-api';
import { FormErrorSummary } from '../../auth/form-error-summary';
import {
  getOfferErrorField,
  getOfferErrorStep,
  validateJobOfferBasics,
} from '../job-offer-form-validation';

export const transformCreateJobOfferData = (data: AddJobOfferFormValues | any) => {
  // Never mutate the form snapshot: a failed request must be safe to retry.
  data = { ...data };
  data.driving_licences =
    data.hasDrivingLicence === false
      ? []
      : (data.driving_licences?.map((driving_licence: any) => ({
          id: driving_licence.id,
        })) as any) || [];
  data.categories = data.categories
    ? data.categories.map((category: any) => ({ id: category.value }))
    : [];
  data.languages = data.languages
    ?.filter((language: any) => language?.language)
    .map((lang: any) => {
      return {
        language: { id: lang.language },
        level: lang.level,
      };
    });
  data.max_salary = data.dual_education ? '0' : (data.max_salary || '0').trim().replace(',', '.');
  data.min_salary = data.dual_education ? '0' : (data.min_salary || '0').trim().replace(',', '.');
  data.skills = data.skills
    ?.filter((skill: any) => !!skill.name)
    .map((skill: any) => ({
      name: skill.name,
      level: skill.level || null,
    }));
  data.country = data.country ? { id: data.country } : { id: 'SK' };
  data.employment_type = data.dual_education
    ? { id: 5 }
    : data.employment_types
      ? {
          id: parseInt(data.employment_types),
        }
      : null;
  data.values = [];
  !!data.for_graduates &&
    data.values.push({
      id: 1,
    });
  !!data.for_foreigners &&
    data.values.push({
      id: 2,
    });
  !!data.homeoffice &&
    data.values.push({
      id: 3,
    });
  data.tags = [
    ...(data.tags ?? []).filter((tag: { id: number }) => tag.id !== 4),
    ...(data.dual_education ? [{ id: 4 }] : []),
  ];

  if (data.start_immediately) {
    data.text_fields = [{ id: 1, value: 'true' }];
  } else if (data.text_fields) {
    data.text_fields = data.text_fields.filter((tf: any) => tf?.id !== 1);
  }

  data.date_start = data.date_start ? new Date(data.date_start) : new Date();
  for (const field of [
    'dual_education',
    'employment_types',
    'hasDrivingLicence',
    'start_immediately',
    'for_graduates',
    'for_foreigners',
    'homeoffice',
    'video',
    'educations',
  ]) {
    delete data[field];
  }
  return data;
};

export type AddJobOfferFormValues = z.infer<ReturnType<typeof addJobSchema>>;

export const addJobSchema = (VM: FormValidationMessages) =>
  z
    .object({
      name: z.string().trim().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      categories: z.any().optional(),
      city: z.string().trim().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      employment_types: z.string(),
      homeoffice: z.boolean().optional(),
      min_salary: z.string(),
      max_salary: z.string(),
      date_start: z.any().optional().nullable(),
      contact_email: z
        .string()
        .email(VM.EMAIL_INVALID_VALIDATION_MESSAGE())
        .min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      description: z.string().trim().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      contact_phone: z.string().trim().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      benefit: z.string().optional(),
      qualification: z.string().optional(),
      for_graduates: z.boolean().optional(),
      for_foreigners: z.boolean().optional(),
      dual_education: z.boolean().optional(),
      free_places: z.string().optional(),
      company_name: z.string().trim().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      skills: z.any().optional().nullable(),
      languages: z.any().optional().nullable(),
      driving_licences: z.any().optional().nullable(),
      hasDrivingLicence: z.boolean().optional(),
      video: z.any().optional().nullable(),
      company_contact_url: z.any().optional().nullable(),
      address: z.string().trim().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      street_number: z.string().optional(),
      postcode: z.string().trim().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),

      salary_type: z.any(),

      company: z
        .object({
          id: z.number(),
        })
        .optional(),

      image: z.any().optional(),

      workplace: z.any().optional(),

      suitability: z.any().optional(),

      age_range: z.any().optional(),

      working_hour: z.any().optional(),

      graduate: z.any().optional(),

      discipline: z.any().optional(),

      prerequisite: z.any().optional(),

      web_url: z.string().optional(),

      contact_name: z.string().trim().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      contact_surname: z.string().trim().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),

      attachments: z
        .array(
          z.object({
            value: z.number().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
          }),
        )
        .optional(),

      images: z
        .array(
          z.object({
            value: z.number().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
          }),
        )
        .optional(),

      tags: z
        .array(
          z.object({
            value: z.number().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
          }),
        )
        .optional(),

      text_fields: z
        .array(
          z.object({
            id: z.number().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
            value: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
          }),
        )
        .optional(),

      country: z.string().trim().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),

      // Metadata bucket for additional flags
      metadata: z
        .object({
          date_immediatelly: z.boolean().optional(),
        })
        .optional(),

      start_immediately: z.boolean().optional(),
      // countries: z.array(
      //   z.object({
      //     id: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      //   }),
      // ),

      // gdpr: z.boolean().refine(val => val === true, { message: VM.GDPR_VALIDATION_MESSAGE() }),
      // tos: z.boolean().refine(val => val === true, { message: VM.TOS_VALIDATION_MESSAGE() }),
      // 'g-recaptcha-response': z.string().default('').optional(),
    })
    .superRefine((data, context) => validateJobOfferBasics(data, context, VM));

type AddJobOfferFormProps = {
  workplaces: { value: number; label: string }[];
  suitabilities: { value: number; label: string }[];
  ageRanges: { value: number; label: string }[];
  graduates: { value: number; label: string }[];
  workingHours: { value: number; label: string }[];
  disciplines: { value: number; label: string }[];
  prerequisites: { value: number; label: string }[];
  languages: { value: number; label: string }[];
  drivingLicences: { value: number; label: string }[];
  countries: { value: string; label: string }[];
  employmentTypes: { value: number; label: string }[];
  jobCategories: { value: number; label: string }[];
  salaryTypes: { value: number; label: string }[];
  tags: { value: number; label: string }[];
};

export function AddJobOfferForm(props: AddJobOfferFormProps) {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);

  const { formValidationMessages } = useFormValidationMessages(t);

  const { me, isInitialized } = useCustomerStore(state => state);
  const router = useRouter();
  const navigateToProfile = useProfileNavigation();

  // Payment dialog state
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [createdOfferSlug, setCreatedOfferSlug] = useState<string>('');
  const [createdOfferIsDual, setCreatedOfferIsDual] = useState(false);
  const [formDisabled, setFormDisabled] = useState(false);
  const submitInFlight = useRef(false);
  const { toast } = useToast();

  // Fetch payment methods
  const { data: payments } = useQuery({
    queryKey: ['cart-payments', locale, me?.authToken ?? null],
    queryFn: () => fetchPaymentTypes({ locale, authToken: me?.authToken || '' }).then(r => r.data),
    enabled: isInitialized && !!me?.authToken,
  });

  // Fetch job offer product
  const { data: offerProduct } = useQuery({
    queryKey: ['job-offer-product', locale, 10, me?.authToken ?? null],
    queryFn: async () => {
      const slugRes = await fetchProducts({
        locale,
        authToken: me?.authToken,
        query: { where: { id: { in: [10] } } },
      }).then(r => r.data[0]?.slug);
      if (!slugRes) return;
      const detail = await fetchSlugDetail({
        locale,
        slug: slugRes,
        authToken: me?.authToken,
        cache: 'no-store',
      });
      return detail['App\\Models\\Product'] as ProductDetailData;
    },
    enabled: isInitialized && !!me?.authToken,
  });

  // Fetch top product
  const { data: topProduct } = useQuery({
    queryKey: ['job-top-product', locale, 9, me?.authToken ?? null],
    queryFn: async () => {
      const slugRes = await fetchProducts({
        locale,
        authToken: me?.authToken,
        query: { where: { id: { in: [9] } } },
      }).then(r => r.data[0]?.slug);
      if (!slugRes) return;
      const detail = await fetchSlugDetail({
        locale,
        slug: slugRes,
        authToken: me?.authToken,
        cache: 'no-store',
      });
      return detail['App\\Models\\Product'] as ProductDetailData;
    },
    enabled: isInitialized && !!me?.authToken,
  });

  useEffect(() => {
    if (!isInitialized) {
      return;
    }
    if (!me || !me.customer || me.customer.customer_type?.type !== 'company') {
      router.push('/');
    }
  }, [isInitialized, me, router]);

  const form = useForm<z.infer<ReturnType<typeof addJobSchema>>>({
    mode: 'onChange',
    resolver: zodResolver(addJobSchema(formValidationMessages)),
    defaultValues: {
      name: '',
      description: '',
      benefit: '',
      qualification: '',
      contact_phone: me?.customer?.phone || '',
      contact_email: me?.customer?.email || '',
      country:
        me?.customer?.country?.id?.toString() ||
        (props.countries.length === 1 ? props.countries[0].value : ''),
      min_salary: '',
      max_salary: '',
      skills: [],
      languages: [],
      driving_licences: [],
      employment_types: '',
      web_url: '',
      free_places: '1',
      company_name: me?.customer?.name || '',
      address: me?.customer?.address || '',
      street_number: me?.customer?.street_number || '',
      postcode: me?.customer?.postcode || '',
      city: '',
      salary_type: [],
      workplace: [],
      suitability: [],
      age_range: [],
      working_hour: [],
      graduate: [],
      discipline: [],
      prerequisite: [],
      tags: [],
      contact_name: me?.customer?.contact_name ?? '',
      contact_surname: me?.customer?.contact_surname ?? '',
      video: '',
      company_contact_url: '',
      metadata: {
        date_immediatelly: false,
      },
    },
  });

  useEffect(() => {
    if (isInitialized) {
      form.setValue('contact_phone', me?.customer?.phone || '');
      form.setValue('contact_email', me?.customer?.email || '');
      form.setValue('contact_name', me?.customer?.contact_name || '');
      form.setValue('contact_surname', me?.customer?.contact_surname || '');
      form.setValue('company_name', me?.customer?.name || '');
      form.setValue('address', me?.customer?.address || '');
      form.setValue('street_number', me?.customer?.street_number || '');
      form.setValue('postcode', me?.customer?.postcode || '');
      if (!form.getValues('country'))
        form.setValue('country', me?.customer?.country?.id?.toString() || '');
      form.setValue('city', me?.customer?.city || '');
    }
  }, [isInitialized]);

  const {
    fields: languageFields,
    append: appendLanguage,
    remove: removeLanguage,
  } = useFieldArray({
    control: form.control,
    name: 'languages',
  });

  const languages = form.watch('languages') || [];

  const [currentStep, setCurrentStep] = React.useState(0);
  const [currentErrorStep, setCurrentErrorStep] = React.useState<
    keyof typeof AddOfferStepsBuckets | null
  >(null);

  const checkIfStepInputsValid = async (step: keyof typeof AddOfferStepsBuckets) => {
    const currentBucket = AddOfferStepsBuckets[step];

    if (!currentBucket) return true; // No fields to validate

    // Trigger validation only for fields in the current bucket
    const isValid = await form.trigger(currentBucket as any, { shouldFocus: true });

    // Check for errors (handle nested schemas with deepGet if needed)
    const hasErrors = currentBucket.some(field => {
      const error = deepGet(form.formState.errors, field);
      return !!error;
    });

    if (!isValid || hasErrors) {
      setCurrentErrorStep(step);
      return false;
    }
    setCurrentErrorStep(null);
    return true; // Step is valid
  };

  useEffect(() => {
    const currentLanguages = form.getValues('languages');

    // Append only if currentLanguages and languageFields are both empty
    if ((!currentLanguages || currentLanguages.length === 0) && languageFields.length === 0) {
      appendLanguage({
        language: null,
        level: null,
      });
    }
  }, []);

  const handleSubmit = form.handleSubmit(
    async data => {
      if (!me || !me.authToken) {
        form.setError('root.server', {
          message: t('Prihlásenie vypršalo. Prihláste sa znova pred uložením ponuky.'),
        });
        return;
      }
      if (formDisabled || submitInFlight.current) return;
      submitInFlight.current = true;
      setFormDisabled(true);
      form.clearErrors('root');
      try {
        if (data.video) {
          const formData = new FormData();

          formData.append('file', data.video);

          try {
            const res = await uploadSecretFile(formData, me?.authToken || '');
            data.company_contact_url = res.data.id.toString() as any;
          } catch (e) {
            form.setError('video', {
              message: t('Video sa nepodarilo nahrať. Skúste to znova alebo ho odstráňte.'),
            });
            throw e;
          }
        }
        const transformedData = transformCreateJobOfferData(data);
        const res: any = await createOffer(transformedData, me?.authToken, locale);
        // Store the created offer slug and show payment dialog

        // Try different possible locations for the slug
        const offerSlug = res?.data?.slug || res?.slug || res?.data?.data?.slug;

        // Always show payment dialog after creation, even for dual education offers
        if (offerSlug && offerProduct && topProduct) {
          setCreatedOfferSlug(offerSlug);
          setCreatedOfferIsDual(
            transformedData.tags?.some((tag: { id: number }) => tag.id === 4) ?? false,
          );
          setShowPaymentDialog(true);
          // Keep form disabled until dialog is closed
        } else {
          setFormDisabled(false);
          navigateToProfile(ROUTES.PROFILE() + '?initStep=company-job-offers');
        }
      } catch (err: any) {
        Object.keys(err.fields || {}).forEach((key: any) => {
          if (key === 'city') {
            form.setError(key, {
              message: 'Miesto výkonu práce musí byť mesto vo vybranej krajine',
            });
            setCurrentErrorStep(0);
            return;
          }
          form.setError(getOfferErrorField(key) as any, {
            message: err.fields[key][0],
          });
        });
        form.setError('root.server', {
          message: t('Ponuku sa nepodarilo uložiť. Skontrolujte údaje a skúste to znova.'),
        });
        const errorStep = getOfferErrorStep(err.fields, AddOfferStepsBuckets);
        setCurrentStep(errorStep);
        setCurrentErrorStep(errorStep);
        toast({
          title: t('text2102'),
          variant: 'destructive',
          description: t('text2103'),
          duration: 5000,
        });
        setFormDisabled(false);
        submitInFlight.current = false;
      }
    },
    errors => {
      const errorStep = getOfferErrorStep(errors, AddOfferStepsBuckets);
      setCurrentStep(errorStep);
      setCurrentErrorStep(errorStep);
    },
  );

  const handlePaymentSuccess = () => {
    setShowPaymentDialog(false);
    setFormDisabled(false);
    navigateToProfile(ROUTES.PROFILE() + '?initStep=company-job-offers');
  };

  const handlePaymentLater = () => {
    setShowPaymentDialog(false);
    setFormDisabled(false);
    navigateToProfile(ROUTES.PROFILE() + '?initStep=company-job-offers');
  };

  // When payment dialog closes, always redirect to profile
  useEffect(() => {
    if (!showPaymentDialog && createdOfferSlug) {
      setFormDisabled(false);
      navigateToProfile(ROUTES.PROFILE() + '?initStep=company-job-offers');
    }
  }, [showPaymentDialog, createdOfferSlug, navigateToProfile]);

  const handleSetCurrentStep = async (step: number) => {
    const isValid = await checkIfStepInputsValid(currentStep as keyof typeof AddOfferStepsBuckets);
    if (isValid) {
      setCurrentStep(step);
    }
  };

  return (
    <FormProvider {...form}>
      <form
        className={cn('flex w-full items-start justify-start gap-2 py-4', 'ea-add-job-offer-form')}
        onSubmit={handleSubmit}
        style={formDisabled ? { pointerEvents: 'none', opacity: 0.5 } : {}}
      >
        <AddOfferStepper
          currentStep={currentStep}
          setCurrentStep={handleSetCurrentStep}
          currentErrorStep={currentErrorStep}
        />
        <div className="flex w-full flex-col gap-4">
          {isInitialized &&
            me &&
            (!me.customer.address?.trim() || !me.customer.postcode?.trim()) && (
              <p role="alert">
                {t('Pred pridaním ponuky doplňte adresu a PSČ vo firemnom profile.')}
              </p>
            )}
          <FormErrorSummary errors={form.formState.errors} />
          {currentStep === 0 && (
            <AddOfferStep0
              categories={props.jobCategories}
              employmentTypes={props.employmentTypes}
            />
          )}
          {currentStep === 1 && <AddOfferStep1 />}
          {currentStep === 2 && <AddOfferStep2 />}
          {currentStep === 3 && <AddOfferStep3 />}
          {currentStep === 4 && <AddOfferStep4 />}
          <AddOfferControls
            currentStep={currentStep}
            setCurrentStep={handleSetCurrentStep}
            handleSubmit={handleSubmit}
            disabled={formDisabled}
          />
        </div>
      </form>
      {offerProduct && topProduct && (
        <JobOfferPayOfferDialog
          isOpen={showPaymentDialog}
          setIsOpen={setShowPaymentDialog}
          offerProduct={offerProduct}
          topProduct={topProduct}
          payments={payments}
          slug={createdOfferSlug}
          offerSlug={createdOfferSlug}
          onSuccess={handlePaymentSuccess}
          onLater={handlePaymentLater}
          isDual={createdOfferIsDual}
        />
      )}
    </FormProvider>
  );
}
